import useDashboardLandingDelay from './useDashboardLandingDelay';
import useIsInOAPhase from './useIsInOAPhase';

import useCalendlyCallData from 'services/calendly/queries/useCalendlyCallData';
import useCalendlyLinksByRole from 'services/calendly/queries/useCalendlyLinksByRole';
import useCalendlyUrls from 'modules/common/hooks/useCalendlyUrls';
import useLaunchpadSchema from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema';
import {
    COMPLETE_ACCOUNTING_SETUP,
    COMPLETE_PAYROLL_SETUP,
} from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema/constants';
import { useMember } from 'modules/common/queries/member';
import { TaskStatus } from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';
import { isInOnboardingStages } from 'modules/common/utils/memberStage';
import { getOnboardingType } from 'modules/common/components/CSatSurvey/OnboardingSetupCSAT/utils';

// Call type for onboarding calls
export const ONBOARDING_CALL_QUERY_KEY = 'onboardingCall';

// Give a member a break after a call before offering them another one
const RECENT_CALL_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export enum CallType {
    EM = 'em',
    OA = 'oa',
}

interface DetermineCallTypeParams {
    isAccountingPayrollComplete: boolean;
    isEMEnabled: boolean;
    isOAEnabled: boolean;
    isLaunchpadActive: boolean;
    isInOAPhase: boolean;
}

/**
 * Extracts the event type slug from a Calendly URL
 * e.g., "https://calendly.com/collective-hub/collective-call-with-maira" → "collective-call-with-maira"
 */
function getSlugFromCalendlyUrl(url: string): string {
    try {
        const { pathname } = new URL(url);
        const parts = pathname.split('/').filter(Boolean);
        return parts[parts.length - 1] || '';
    } catch {
        return '';
    }
}

/**
 * Returns EM, OA, or null based on launchpad phase and feature flags
 */
function determineCallType(params: DetermineCallTypeParams): CallType | null {
    const { isAccountingPayrollComplete, isEMEnabled, isOAEnabled, isLaunchpadActive, isInOAPhase } = params;

    if (!isLaunchpadActive || isAccountingPayrollComplete) {
        return null;
    }

    if (isInOAPhase && isOAEnabled) {
        return CallType.OA;
    }
    if (!isInOAPhase && isEMEnabled) {
        return CallType.EM;
    }

    return null;
}

/**
 * Hook to manage the onboarding call banner state
 *
 * An upcoming onboarding call the member already booked (EM or OA) is shown first, regardless of phase.
 * Otherwise, unless the member had a call in the last day, determines whether to offer an EM or OA call based on:
 * - Launchpad task status (active, not all completed)
 * - Accounting & payroll progress
 * - Feature flag status (EnableEMCallBanner, EnableOACallBanner)
 * - Current onboarding phase (EM vs OA based on bank account completion)
 *
 * Uses useCalendlyCallData for fetching call data and polling.
 */
export default function useOnboardingCallBanner() {
    const { data: member } = useMember();
    const { launchpadItems, allTasksCompleted } = useLaunchpadSchema();

    // Check if 3 days have passed since user first landed on dashboard
    const isOnboarding = isInOnboardingStages(member?.stage);
    const onboardingType = getOnboardingType(member?.usertype);
    const { hasThreeDaysPassed, isPending: isDelayPending } = useDashboardLandingDelay({
        enableFormation: isOnboarding && onboardingType === 'formation',
        enableTakeover: isOnboarding && onboardingType === 'takeover',
    });

    // Feature flags
    const isEMEnabled = useStatsigGate(FeatureGates.EnableEMCallBanner);
    const isOAEnabled = useStatsigGate(FeatureGates.EnableOACallBanner);

    // Calendly URLs and slugs
    const { emCallUrl, oaCallUrl, emSlug, oaSlug } = useCalendlyUrls();
    const { data: emSpecialist } = useCalendlyLinksByRole('Enrollment Manager');
    const assignedEMCalendlyUrl = emSpecialist?.calendars?.em?.schedule_url;

    // Check if launchpad is active
    const isLaunchpadActive = launchpadItems.length > 0 && !allTasksCompleted;

    // Get the launchpad tasks
    const { isInOAPhase } = useIsInOAPhase();
    const accountingTask = launchpadItems.find((item) => item.template_id === COMPLETE_ACCOUNTING_SETUP.id);
    const payrollTask = launchpadItems.find((item) => item.template_id === COMPLETE_PAYROLL_SETUP.id);

    const isAccountingComplete =
        accountingTask?.status === TaskStatus.COMPLETED || accountingTask?.allSubtasksCompleted === true;
    const isPayrollComplete =
        payrollTask?.status === TaskStatus.COMPLETED || payrollTask?.allSubtasksCompleted === true;

    const isAccountingPayrollComplete = payrollTask ? isAccountingComplete && isPayrollComplete : isAccountingComplete;

    // The call type the member's launchpad phase offers when nothing is booked
    const phaseCallType = determineCallType({
        isAccountingPayrollComplete,
        isEMEnabled,
        isOAEnabled,
        isLaunchpadActive,
        isInOAPhase,
    });

    // EM calls use the assigned EM's own calendly link when it has a valid slug, else round-robin.
    // A booked EM call keeps the link it was booked through.
    const assignedEMSlug = assignedEMCalendlyUrl ? getSlugFromCalendlyUrl(assignedEMCalendlyUrl) : '';
    const getCallConfig = (type: CallType | null, bookedSlug?: string) => {
        if (type === CallType.EM) {
            const useAssignedEM = !!assignedEMSlug && (!bookedSlug || bookedSlug.startsWith(assignedEMSlug));
            return useAssignedEM
                ? { slug: assignedEMSlug, calendlyUrl: assignedEMCalendlyUrl as string }
                : { slug: emSlug, calendlyUrl: emCallUrl };
        }
        if (type === CallType.OA) {
            return { slug: oaSlug, calendlyUrl: oaCallUrl };
        }
        return { slug: '', calendlyUrl: '' };
    };

    // A call the member already booked is shown whatever their phase, so look across every onboarding call type
    const isBookedCallLookupEnabled = isOnboarding && (isEMEnabled || isOAEnabled);
    const callData = useCalendlyCallData(ONBOARDING_CALL_QUERY_KEY, [assignedEMSlug, emSlug, oaSlug], {
        enabled: isBookedCallLookupEnabled,
    });
    const hasBookedCall = isBookedCallLookupEnabled && callData.isScheduled;
    // Offer a new call only once the lookup has succeeded: a pending or failed lookup is not proof nothing is booked
    const canOfferNewCall = !isBookedCallLookupEnabled || callData.isSuccess;

    const bookedCallType = callData.slug && oaSlug && callData.slug.startsWith(oaSlug) ? CallType.OA : CallType.EM;
    const callType = hasBookedCall ? bookedCallType : phaseCallType;
    const { slug, calendlyUrl } = getCallConfig(callType, hasBookedCall ? callData.slug : undefined);

    const hadRecentCall =
        !!callData.lastCallEndedAt &&
        Date.now() - new Date(callData.lastCallEndedAt).getTime() < RECENT_CALL_COOLDOWN_MS;

    const shouldShow =
        hasBookedCall ||
        (phaseCallType !== null &&
            !!calendlyUrl &&
            !isDelayPending &&
            hasThreeDaysPassed &&
            canOfferNewCall &&
            !hadRecentCall);

    return {
        shouldShow,
        callType,
        slug,
        isScheduled: hasBookedCall,
        scheduledTime: callData.scheduledTime,
        endTime: callData.endTime,
        rescheduleUrl: callData.rescheduleUrl || calendlyUrl,
        joinUrl: callData.joinUrl,
        calendlyUrl,
        isPending: callData.isPending,
        refetch: callData.refetch,
        member,
        startPolling: callData.startPolling,
        stopPolling: callData.stopPolling,
    };
}
